import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs/operators';
import { LabelLogo, LabelLogoFit, LabelLogoService } from '@app/services/label-logo/label-logo.service';

interface LabelTemplate {
  key: string;
  label: string;
  path: string;
}

/** The mock is drawn at a tenth of the label size, twips map straight to pixels */
const PREVIEW_SCALE = 10;

@Component({
  selector: 'app-label-preview',
  templateUrl: './label-preview.component.html',
  styleUrls: ['./label-preview.component.scss']
})
export class LabelPreviewComponent implements OnInit, AfterViewInit {
  @ViewChild('mock') mockCanvas: ElementRef<HTMLCanvasElement>;

  public templates: LabelTemplate[] = [
    { key: 'qr', label: 'labelLogo.templateQr', path: 'assets/dymo/ShippingLabelQROnly.xml' },
    { key: 'barcode', label: 'labelLogo.templateBarcode', path: 'assets/dymo/ShippingLabelBarcodeOnly.xml' }
  ];
  public selectedTemplate: LabelTemplate = this.templates[0];
  public isLoading: boolean = true;
  public hasLogo: boolean = false;
  public fit: LabelLogoFit | null = null;
  /** Set when DYMO itself rendered the preview, so it is what actually prints */
  public isPrinterPreview: boolean = false;
  public printerPreviewSrc: string = '';

  private logo: LabelLogo | null = null;
  private viewReady: boolean = false;

  constructor(
    private http: HttpClient,
    private _labelLogo: LabelLogoService
  ) { }

  ngOnInit(): void {
    this.reload();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  public onSelectTemplate(template: LabelTemplate): void {
    this.selectedTemplate = template;
    this.render();
  }

  /**
   * Re-reads the stored logo. Called on load and whenever the upload changes.
   */
  public reload(): void {
    this.isLoading = true;
    this._labelLogo.resolveLogoSource().subscribe({
      next: (source) => {
        this._labelLogo.prepare(source).then(logo => {
          this.logo = logo;
          this.hasLogo = !!logo;
          // Keep what the printer reads in step with what is previewed
          this._labelLogo.cache(logo);
          this.isLoading = false;
          this.render();
        });
      },
      error: () => {
        this.logo = null;
        this.hasLogo = false;
        this.isLoading = false;
        this.render();
      }
    });
  }

  private render(): void {
    if(!this.viewReady || this.isLoading){
      return;
    }

    this.http.get(this.selectedTemplate.path, { responseType: 'text' }).pipe(first()).subscribe({
      next: (template) => this.draw(this.fillTemplate(template)),
      error: () => { this.fit = null; }
    });
  }

  private fillTemplate(template: string): string {
    const logo = this.logo ? this.logo.dataUrl.replace(/^data:image\/[a-z+]+;base64,/i, '') : '';
    let xml = logo
      ? template.replace('***LogoPlaceholder***', () => logo)
      : template.replace(/<!--LOGO_START-->[\s\S]*?<!--LOGO_END-->/, '');

    // QR templates carry two objects for the same code: the image that is
    // actually printed and the barcode kept as a fallback. printerCode drops
    // one of them before printing, so the preview has to as well - otherwise
    // both are drawn on top of each other. Keep the image, since that is what
    // prints whenever the PNG loads, and it owns the real bounds.
    xml = xml.replace(/<!--QRBARCODE_START-->[\s\S]*?<!--QRBARCODE_END-->/, '');

    const values: { [key: string]: string } = {
      '***ProductNamePlaceholder***': 'Sample Product',
      '***ProdCodePlaceholder***': 'SKU: SAMPLE-001',
      '***PricePlaceholder***': 'Price: $9.99',
      '***AssetNumberPlaceholder***': '',
      '***CategoryPlaceholder***': 'Type: Product',
      '***ProdQrCodePlaceholder***': '0|0',
      '***ProdBarcodePlaceholder***': 'SAMPLE-001'
    };

    Object.keys(values).forEach(placeholder => {
      xml = xml.split(placeholder).join(values[placeholder]);
    });

    return xml;
  }

  private draw(xml: string): void {
    const label = new DOMParser().parseFromString(xml, 'application/xml');
    this.measureLogo(label);

    if(this.drawWithPrinter(xml)){
      return;
    }

    this.isPrinterPreview = false;
    this.printerPreviewSrc = '';
    this.drawMock(label);
  }

  /**
   * DYMO can rasterise the label itself, which is the only preview guaranteed
   * to match the print. Falls back to the mock when it is not installed.
   */
  private drawWithPrinter(xml: string): boolean {
    const framework = (window as any).dymo?.label?.framework;
    if(!framework){
      return false;
    }

    try {
      const printers = framework.getLabelWriterPrinters();
      const printerName = printers && printers.length ? printers[0].name : undefined;
      const png = framework.openLabelXml(xml).render(null, printerName);
      if(!png){
        return false;
      }

      this.printerPreviewSrc = 'data:image/png;base64,' + png;
      this.isPrinterPreview = true;
      return true;
    } catch (e) {
      return false;
    }
  }

  private measureLogo(label: Document): void {
    // Without a logo the empty state message covers it, no size to report
    if(!this.logo){
      this.fit = null;
      return;
    }

    const bounds = this.findLogoBounds(label);
    this.fit = bounds ? this._labelLogo.describeFit(this.logo, bounds.width, bounds.height) : null;
  }

  private findLogoBounds(label: Document): { width: number, height: number } | null {
    const objects = Array.from(label.getElementsByTagName('ObjectInfo'));
    for(const object of objects){
      // The QR image is an ImageObject too, and reporting its box as the logo
      // fit would quote a printed size the logo never gets.
      if(object.getElementsByTagName('ImageObject').length && !this.isQrImage(object)){
        const bounds = object.getElementsByTagName('Bounds')[0];
        if(bounds){
          return {
            width: parseFloat(bounds.getAttribute('Width')),
            height: parseFloat(bounds.getAttribute('Height'))
          };
        }
      }
    }

    return null;
  }

  private drawMock(label: Document): void {
    const canvas = this.mockCanvas?.nativeElement;
    const context = canvas?.getContext('2d');
    if(!context){
      return;
    }

    const size = this.readLabelSize(label);
    canvas.width = size.width / PREVIEW_SCALE;
    canvas.height = size.height / PREVIEW_SCALE;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const objects = Array.from(label.getElementsByTagName('ObjectInfo'));
    objects.forEach(object => this.drawObject(context, object));
  }

  private readLabelSize(label: Document): { width: number, height: number } {
    const rectangle = label.getElementsByTagName('RoundRectangle')[0];
    const width = rectangle ? parseFloat(rectangle.getAttribute('Width')) : 3060;
    const height = rectangle ? parseFloat(rectangle.getAttribute('Height')) : 5760;
    const orientation = label.getElementsByTagName('PaperOrientation')[0]?.textContent;

    // Object bounds are written in the rotated space of a landscape label
    return orientation === 'Landscape'
      ? { width: height, height: width }
      : { width: width, height: height };
  }

  private drawObject(context: CanvasRenderingContext2D, object: Element): void {
    const bounds = object.getElementsByTagName('Bounds')[0];
    if(!bounds){
      return;
    }

    const x = parseFloat(bounds.getAttribute('X')) / PREVIEW_SCALE;
    const y = parseFloat(bounds.getAttribute('Y')) / PREVIEW_SCALE;
    const width = parseFloat(bounds.getAttribute('Width')) / PREVIEW_SCALE;
    const height = parseFloat(bounds.getAttribute('Height')) / PREVIEW_SCALE;

    if(object.getElementsByTagName('ImageObject').length){
      // Not every image is the logo: QR templates print the code as an image
      // too, and drawing the logo into its box covered most of the code.
      if(this.isQrImage(object)){
        this.drawQrPlaceholder(context, x, y, width, height);
      } else {
        this.drawLogo(context, x, y, width, height);
      }
      return;
    }

    if(object.getElementsByTagName('BarcodeObject').length){
      this.drawBarcodePlaceholder(context, object, x, y, width, height);
      return;
    }

    this.drawText(context, object, x, y, width, height);
  }

  private drawLogo(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
    if(!this.logo){
      return;
    }

    const scale = Math.min(width / this.logo.width, height / this.logo.height);
    const drawWidth = this.logo.width * scale;
    const drawHeight = this.logo.height * scale;

    const image = new Image();
    image.onload = () => context.drawImage(image, x, y + (height - drawHeight) / 2, drawWidth, drawHeight);
    image.src = this.logo.dataUrl;
  }

  private drawText(context: CanvasRenderingContext2D, object: Element, x: number, y: number, width: number, height: number): void {
    const text = object.getElementsByTagName('String')[0]?.textContent || '';
    if(!text.trim()){
      return;
    }

    const font = object.getElementsByTagName('Font')[0];
    const points = font ? parseFloat(font.getAttribute('Size')) : 10;
    const bold = font && font.getAttribute('Bold') === 'True';
    // A point is twenty twips, which the preview scale then divides down
    let pixels = points * 20 / PREVIEW_SCALE;

    context.fillStyle = '#000000';
    context.textBaseline = 'middle';
    context.font = `${bold ? 'bold ' : ''}${pixels}px Arial`;

    // The templates shrink text to fit, so the preview has to as well
    while(context.measureText(text).width > width && pixels > 4){
      pixels -= 0.5;
      context.font = `${bold ? 'bold ' : ''}${pixels}px Arial`;
    }

    context.fillText(text, x, y + height / 2);
  }

  /** The image object QR templates print the code into, rather than the logo */
  private isQrImage(object: Element): boolean {
    return object.getElementsByTagName('Name')[0]?.textContent?.trim() === 'QRIMAGE';
  }

  private drawQrPlaceholder(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
    // Stands in for the real code, so it only has to show placement and size.
    // It still has to read as a QR code at a glance: the flat checker pattern
    // this replaced looked like a broken code and got reported as a bug. The
    // three finder squares and the timing rows are what the eye recognises.
    const modules = 21;
    const size = Math.min(width, height);
    const cell = size / modules;
    const originX = x + (width - size) / 2;
    const originY = y + (height - size) / 2;
    const finders = [[0, 0], [modules - 7, 0], [0, modules - 7]];

    const fill = (column: number, row: number) =>
      context.fillRect(originX + column * cell, originY + row * cell, cell, cell);

    // The finder square plus the quiet ring around it that stays empty
    const reserved = (column: number, row: number) => finders.some(
      ([zoneX, zoneY]) => column >= zoneX - 1 && column <= zoneX + 7 && row >= zoneY - 1 && row <= zoneY + 7
    );

    context.fillStyle = '#000000';

    finders.forEach(([zoneX, zoneY]) => {
      for(let row = 0; row < 7; row++){
        for(let column = 0; column < 7; column++){
          const ring = row === 0 || row === 6 || column === 0 || column === 6;
          const core = row >= 2 && row <= 4 && column >= 2 && column <= 4;
          if(ring || core){
            fill(zoneX + column, zoneY + row);
          }
        }
      }
    });

    // Timing patterns: the alternating row and column joining the finders
    for(let index = 8; index < modules - 8; index++){
      if(index % 2 === 0){
        fill(index, 6);
        fill(6, index);
      }
    }

    // Deterministic fill for everything else, at roughly the density a real
    // code carries so it does not read as half empty. Hashed rather than a
    // modulo of row and column terms: those share a factor often enough to
    // blank out whole rows on a regular cycle, which is visible as banding.
    const isSet = (column: number, row: number) => {
      let hash = ((row + 1) * 73856093) ^ ((column + 1) * 19349663);
      hash = (hash ^ (hash >>> 13)) >>> 0;
      return hash % 100 < 45;
    };

    for(let row = 0; row < modules; row++){
      for(let column = 0; column < modules; column++){
        if(row === 6 || column === 6 || reserved(column, row)){
          continue;
        }
        if(isSet(column, row)){
          fill(column, row);
        }
      }
    }
  }

  private drawBarcodePlaceholder(context: CanvasRenderingContext2D, object: Element, x: number, y: number, width: number, height: number): void {
    const type = object.getElementsByTagName('Type')[0]?.textContent || '';
    context.fillStyle = '#000000';

    if(type.indexOf('QR') >= 0){
      this.drawQrPlaceholder(context, x, y, width, height);
      return;
    }

    let position = x;
    let index = 0;
    while(position < x + width){
      const barWidth = 1 + (index % 3);
      if(index % 2 === 0){
        context.fillRect(position, y, barWidth, height * 0.8);
      }
      position += barWidth;
      index++;
    }
  }
}
